import React from 'react'
import { Element, Icon } from 'react-bulma-components'
import { Mission } from '../../../api/missions'
import { useUpdateMutation } from '../../../queries/missions'
import { formatCompleteDateTime } from '../../../utils/date'
import Avatar from '../../avatar/avatar'
import ConfirmationModal, {
  ConfirmationModalBaseProps,
} from '../../confirmation-modal/confirmation-modal'
import { isAfter } from 'date-fns'
import { hasAllWorkPeriodsClocked } from '../../../utils/missions'
import { WarningIcon } from '../../icons'

interface MissionValidateConfirmationProps extends ConfirmationModalBaseProps {
  mission: Mission
}

/**
 * Full-mission validation dialog. Visibility is controlled by the parent via `show`
 * (e.g. `useModal`); do not derive visibility from `mission` alone or the modal opens on load.
 */
const MissionValidateConfirmation: React.FunctionComponent<MissionValidateConfirmationProps> = ({
  show,
  mission,
  hide,
}) => {
  const mutation = useUpdateMutation()

  const workPeriods = mission.workPeriods
  const lastWorkPeriod = workPeriods[workPeriods.length - 1]

  const onConfirm = async () => {
    await mutation.mutate({
      _id: mission._id,
      isValidated: true,
    })

    hide()
  }

  const hasNotStartedWorkPeriod =
    lastWorkPeriod && isAfter(new Date(lastWorkPeriod.start.date), new Date())

  const hasNotAllWorkPeriodsClocked = !hasAllWorkPeriodsClocked({ ...mission, workPeriods })

  return (
    <ConfirmationModal
      show={show}
      onConfirm={onConfirm}
      onCancel={hide}
      confirmButtonDataTest="validate-mission-confirmation-confirm"
    >
      <Element mb={3}>
        <Element renderAs="p" textWeight="bold" mb={2}>
          Vous pouvez valider l'entièreté d'une mission si l'ensemble de ses Journées de Travail ne
          sont pas vides.
        </Element>
        <Element renderAs="p" mb={2}>
          Chaque journée doit avoir au moins l'une des informations suivantes :
        </Element>
        <Element renderAs="ul">
          <li>Soit un pointage de début et de fin</li>
          <li>Soit un événement</li>
          <li>Soit être en Abandon de Poste</li>
        </Element>
      </Element>
      <Element renderAs="hr" my={3} />
      <Element>
        <Element renderAs="p" mb={3}>
          Valider l'entièreté de la Mission : <br />
        </Element>
        {workPeriods[0] && lastWorkPeriod && (
          <Element renderAs="strong">
            <Avatar user={mission.user} />
            <br />
            Du {formatCompleteDateTime(workPeriods[0].start.date)} au{' '}
            {formatCompleteDateTime(lastWorkPeriod.end.date)}
          </Element>
        )}
      </Element>
      {(hasNotStartedWorkPeriod || hasNotAllWorkPeriodsClocked) && (
        <Element mt={4}>
          <Icon.Text>
            <Icon>
              <WarningIcon />
            </Icon>
            <Element renderAs="span" textWeight="bold">
              Attention
            </Element>
          </Icon.Text>
          {hasNotStartedWorkPeriod && <Element>La semaine n'est pas encore terminée</Element>}
          {hasNotAllWorkPeriodsClocked && (
            <Element>Toutes les journées de travail n'ont pas été pointées</Element>
          )}
        </Element>
      )}
    </ConfirmationModal>
  )
}

export default MissionValidateConfirmation
